import React, { useState, useRef, useEffect } from 'react';

const TABS_GESTRACOO = ['dashboard', 'matriz', 'materiales', 'documentos', 'progreso'];

const TABS_PROGRAMA = ['programa-dashboard', 'programa', 'programa-progreso'];

const TABS_CALIDAD = ['calidad-dashboard', 'calidad-registros', 'calidad-matriz', 'calidad-observaciones', 'calidad-seguimiento'];

function inferirModulo(tab) {
  if (!tab) return null;
  if (tab === 'proyecto-resumen') return null; // resumen = pantalla selector
  if (tab.startsWith('recepcion-')) return 'recepcion';
  if (TABS_GESTRACOO.includes(tab)) return 'gestracoo';
  if (TABS_PROGRAMA.includes(tab)) return 'programa';
  if (TABS_CALIDAD.includes(tab)) return 'calidad';
  return null;
}

const Sidebar = ({
  activeTab,
  setActiveTab,
  projectName,
  projectUser,
  projectManager,
  teamMembers,
  usuarioActual,
  appsVisibles,
  proyectos,
  proyectoActivo,
  onCambiarProyecto,
  onNuevoProyecto,
  modoGlobal,
  onLogout
}) => {
  const [dropdownAbierto, setDropdownAbierto] = useState(false);
  const dropdownRef = useRef(null);

  const [moduloActivo, setModuloActivo] = useState(() => {
    const guardado = localStorage.getItem('moduloActivo');
    // Compatibilidad: el módulo "certificaciones" se renombró a "recepcion".
    if (guardado === 'certificaciones') return 'recepcion';
    if (guardado) return guardado;
    return inferirModulo(activeTab);
  });

  // Persistir elección en localStorage
  useEffect(() => {
    if (moduloActivo) localStorage.setItem('moduloActivo', moduloActivo);
  }, [moduloActivo]);

  // Sincronizar módulo cuando activeTab cambia externamente
  useEffect(() => {
    const modulo = inferirModulo(activeTab);
    if (modulo) setModuloActivo(modulo);
  }, [activeTab]);

  // Si solo un módulo está habilitado, seleccionarlo automáticamente
  useEffect(() => {
    if (!appsVisibles) return;
    const tieneGestracoo = appsVisibles.gestracoo;
    const tieneRecepcion = appsVisibles.recepcion;
    const tienePrograma = appsVisibles.programa ?? true;
    const soloGestracoo = tieneGestracoo && !tieneRecepcion && !tienePrograma;
    const soloRecepcion = !tieneGestracoo && tieneRecepcion && !tienePrograma;
    const soloPrograma = !tieneGestracoo && !tieneRecepcion && tienePrograma;
    if (soloGestracoo && !moduloActivo) setModuloActivo('gestracoo');
    if (soloRecepcion && !moduloActivo) setModuloActivo('recepcion');
    if (soloPrograma && !moduloActivo) setModuloActivo('programa');
  }, [appsVisibles, moduloActivo]);

  useEffect(() => {
    const handleClickFuera = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownAbierto(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  const nombreActivo = modoGlobal
    ? (usuarioActual?.tipoUsuario === 'gerente'
        ? 'Vista Global'
        : (usuarioActual?.nombre || 'Vista Global'))
    : (proyectoActivo?.nombre || projectName || 'GestraCOO');

  const elegirModulo = (modulo) => {
    setModuloActivo(modulo);
    if (modulo === 'gestracoo') setActiveTab('dashboard');
    if (modulo === 'recepcion') setActiveTab('recepcion-matriz');
    if (modulo === 'programa') setActiveTab('programa-dashboard');
    if (modulo === 'calidad') setActiveTab('calidad-dashboard');
  };

  const volverSelector = () => {
    setModuloActivo(null);
    localStorage.removeItem('moduloActivo');
    setActiveTab('proyecto-resumen');
  };

  // Al iniciar sin módulo activo, mostrar siempre el resumen
  useEffect(() => {
    if (!moduloActivo && !modoGlobal) setActiveTab('proyecto-resumen');
  }, [moduloActivo, modoGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        {proyectos && proyectos.length > 0 ? (
          <div className="sidebar-proyecto-selector" ref={dropdownRef}>
            <button
              className="sidebar-proyecto-btn"
              onClick={() => setDropdownAbierto(prev => !prev)}
              title="Cambiar proyecto"
            >
              <div className="sidebar-icon-container" style={{ flexShrink: 0 }}>
                {nombreActivo.charAt(0).toUpperCase()}
              </div>
              <div className="sidebar-project-info" style={{ flex: 1, minWidth: 0 }}>
                <h2 className="sidebar-project-name" style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {nombreActivo}
                </h2>
                {projectUser && (
                  <p className="sidebar-project-user" style={{ margin: 0 }}>{projectUser}</p>
                )}
              </div>
              {(proyectos.length > 1 || usuarioActual?.tipoUsuario === 'propietario' || usuarioActual?.tipoUsuario === 'gerente') && (
                <span style={{ fontSize: '10px', color: '#999', flexShrink: 0 }}>
                  {dropdownAbierto ? '▲' : '▼'}
                </span>
              )}
            </button>

            {dropdownAbierto && (
              <div className="sidebar-proyecto-dropdown">
                {proyectos.map(p => {
                  const pid = p.id || p._id;
                  const activoId = proyectoActivo?.id || proyectoActivo?._id;
                  const esActivo = !modoGlobal && pid === activoId;
                  return (
                    <button
                      key={pid}
                      className={`sidebar-proyecto-opcion ${esActivo ? 'activo' : ''}`}
                      onClick={() => {
                        if ((modoGlobal || !esActivo) && onCambiarProyecto) onCambiarProyecto(p);
                        setDropdownAbierto(false);
                      }}
                    >
                      <span className="sidebar-proyecto-opcion-icono">
                        {esActivo ? '●' : '○'}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.nombre}
                      </span>
                    </button>
                  );
                })}
                {(usuarioActual?.tipoUsuario === 'propietario' || usuarioActual?.tipoUsuario === 'gerente') && (
                  <>
                    <div className="sidebar-proyecto-dropdown-separator" />
                    <button
                      className="sidebar-proyecto-opcion vista-global"
                      onClick={() => {
                        setActiveTab('dashboard-global');
                        setDropdownAbierto(false);
                      }}
                    >
                      <span className="sidebar-proyecto-opcion-icono">
                        {usuarioActual.tipoUsuario === 'gerente' ? '🏢' : '👤'}
                      </span>
                      {usuarioActual.tipoUsuario === 'gerente'
                        ? `Vista Global${usuarioActual.tipoGerente === 'general' ? ' (General)' : ' (Proyecto)'}`
                        : usuarioActual.nombre}
                    </button>
                  </>
                )}
                {usuarioActual?.tipoUsuario === 'propietario' && (
                  <button
                    className="sidebar-proyecto-opcion nuevo-proyecto"
                    onClick={() => {
                      if (onNuevoProyecto) onNuevoProyecto();
                      setDropdownAbierto(false);
                    }}
                  >
                    <span className="sidebar-proyecto-opcion-icono">＋</span>
                    Nuevo Proyecto
                  </button>
                )}
              </div>
            )}

            {projectManager && (
              <p className="sidebar-project-manager" style={{ marginTop: '4px' }}>
                {(() => {
                  if (usuarioActual?.tipoUsuario === 'propietario') {
                    return `Propietario: ${usuarioActual?.nombre || projectUser}`;
                  }
                  const miembro = teamMembers.find(m => {
                    if (m.usuario) {
                      const uid = typeof m.usuario === 'object' ? m.usuario._id : m.usuario;
                      if (uid === usuarioActual?._id || uid === usuarioActual?.id) return true;
                    }
                    if (m.email && usuarioActual?.email && m.email.toLowerCase() === usuarioActual.email.toLowerCase()) return true;
                    return false;
                  });
                  const cargo = miembro?.cargo || miembro?.role || usuarioActual?.tipoUsuario || 'Sin cargo';
                  return `${cargo}: ${usuarioActual?.nombre || projectUser}`;
                })()}
              </p>
            )}
          </div>
        ) : (
          <div className="sidebar-project-icon">
            <div className="sidebar-icon-container">
              {projectName ? projectName.charAt(0).toUpperCase() : 'G'}
            </div>
            <div className="sidebar-project-info">
              <h2 className="sidebar-project-name">{projectName || 'GestraCOO'}</h2>
              {projectUser && <p className="sidebar-project-user">{projectUser}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="tabs-navigation">
        {modoGlobal ? (
          <>
            <div className="sidebar-app-separator">
              <div className="sidebar-app-separator-line"></div>
              <span className="sidebar-app-separator-label" style={{ color: '#111827' }}>VISTA GLOBAL</span>
              <div className="sidebar-app-separator-line"></div>
            </div>
            <button className={`tab-button ${activeTab === 'dashboard-global' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard-global')}>Vista General</button>
            <button className={`tab-button ${activeTab === 'proyectos-equipo' ? 'active' : ''}`} onClick={() => setActiveTab('proyectos-equipo')}>Proyectos</button>
            <button className={`tab-button ${activeTab === 'global-equipo' ? 'active' : ''}`} onClick={() => setActiveTab('global-equipo')}>Equipo</button>
          </>
        ) : moduloActivo === null ? (
          /* ── PANTALLA DE SELECCIÓN DE MÓDULO ── */
          <div className="sidebar-modulo-selector">
            <button
              className={`tab-button ${activeTab === 'proyecto-resumen' ? 'active' : ''}`}
              onClick={() => setActiveTab('proyecto-resumen')}
              style={{ marginBottom: '8px' }}
            >
              Resumen Proyecto
            </button>
            <p className="sidebar-modulo-titulo">Ir a módulo</p>
            {(appsVisibles?.programa ?? true) && (
              <button
                className="sidebar-modulo-btn sidebar-modulo-btn-programa"
                onClick={() => elegirModulo('programa')}
              >
                <span className="sidebar-modulo-btn-label">Programa de Obra</span>
              </button>
            )}
            {(appsVisibles?.calidad ?? true) && (
              <button
                className="sidebar-modulo-btn sidebar-modulo-btn-calidad"
                onClick={() => elegirModulo('calidad')}
              >
                <span className="sidebar-modulo-btn-label">Calidad</span>
              </button>
            )}
            {appsVisibles?.gestracoo && (
              <button
                className="sidebar-modulo-btn sidebar-modulo-btn-gestracoo"
                onClick={() => elegirModulo('gestracoo')}
              >
                <span className="sidebar-modulo-btn-label">Subcontratos<br/>y Materiales</span>
              </button>
            )}
            {appsVisibles?.recepcion && (
              <button
                className="sidebar-modulo-btn sidebar-modulo-btn-recepcion"
                onClick={() => elegirModulo('recepcion')}
              >
                <span className="sidebar-modulo-btn-label">Recepción</span>
              </button>
            )}
          </div>
        ) : (
          /* ── NAVEGACIÓN DEL MÓDULO ELEGIDO ── */
          <>
            <button className="sidebar-modulo-volver" onClick={volverSelector}>
              ← Cambiar módulo
            </button>

            {moduloActivo === 'gestracoo' && appsVisibles?.gestracoo && (
              <>
                <div className="sidebar-app-separator">
                  <div className="sidebar-app-separator-line"></div>
                  <span className="sidebar-app-separator-label">
                    <span style={{ color: '#1a1a1a' }}>Subcontratos</span><span style={{ color: '#4b5563' }}> y Materiales</span>
                  </span>
                  <div className="sidebar-app-separator-line"></div>
                </div>
                <button className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
                <button className={`tab-button ${activeTab === 'matriz' ? 'active' : ''}`} onClick={() => setActiveTab('matriz')}>Subcontratos</button>
                <button className={`tab-button ${activeTab === 'materiales' ? 'active' : ''}`} onClick={() => setActiveTab('materiales')}>Materiales</button>
                <button className={`tab-button ${activeTab === 'documentos' ? 'active' : ''}`} onClick={() => setActiveTab('documentos')}>Documentos</button>
                <button className={`tab-button ${activeTab === 'progreso' ? 'active' : ''}`} onClick={() => setActiveTab('progreso')}>Progreso</button>
              </>
            )}

            {moduloActivo === 'programa' && (appsVisibles?.programa ?? true) && (
              <>
                <div className="sidebar-app-separator">
                  <div className="sidebar-app-separator-line"></div>
                  <span className="sidebar-app-separator-label">
                    <span style={{ color: '#111827' }}>Programa de Obra</span>
                  </span>
                  <div className="sidebar-app-separator-line"></div>
                </div>
                <button className={`tab-button ${activeTab === 'programa-dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('programa-dashboard')}>Dashboard</button>
                <button className={`tab-button ${activeTab === 'programa' ? 'active' : ''}`} onClick={() => setActiveTab('programa')}>Programa</button>
                <button className={`tab-button ${activeTab === 'programa-progreso' ? 'active' : ''}`} onClick={() => setActiveTab('programa-progreso')}>Progreso</button>
              </>
            )}

            {moduloActivo === 'recepcion' && appsVisibles?.recepcion && (
              <>
                <div className="sidebar-app-separator">
                  <div className="sidebar-app-separator-line"></div>
                  <span className="sidebar-app-separator-label">
                    <span style={{ color: '#1a1a1a' }}>Recepción</span>
                  </span>
                  <div className="sidebar-app-separator-line"></div>
                </div>
                <button className={`tab-button ${activeTab === 'recepcion-matriz' ? 'active' : ''}`} onClick={() => setActiveTab('recepcion-matriz')}>Recepción</button>
                <button className={`tab-button ${activeTab === 'recepcion-obra' ? 'active' : ''}`} onClick={() => setActiveTab('recepcion-obra')}>Recepción Obra</button>
                <button className={`tab-button ${activeTab === 'recepcion-rutacritica' ? 'active' : ''}`} onClick={() => setActiveTab('recepcion-rutacritica')}>Ruta Crítica</button>
                <button className={`tab-button ${activeTab === 'recepcion-progreso' ? 'active' : ''}`} onClick={() => setActiveTab('recepcion-progreso')}>Progreso</button>
              </>
            )}

            {moduloActivo === 'calidad' && (appsVisibles?.calidad ?? true) && (
              <>
                <div className="sidebar-app-separator">
                  <div className="sidebar-app-separator-line"></div>
                  <span className="sidebar-app-separator-label" style={{ color: '#111827' }}>Calidad</span>
                  <div className="sidebar-app-separator-line"></div>
                </div>
                <button className={`tab-button ${activeTab === 'calidad-dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('calidad-dashboard')}>Dashboard</button>
                <button className={`tab-button ${activeTab === 'calidad-registros' ? 'active' : ''}`} onClick={() => setActiveTab('calidad-registros')}>Registros</button>

                <div className="sidebar-app-subseparator">
                  <span className="sidebar-app-subseparator-label">Entrega de Departamento</span>
                </div>
                <button className={`tab-button tab-button-sub ${activeTab === 'calidad-matriz' ? 'active' : ''}`} onClick={() => setActiveTab('calidad-matriz')}>Revision R0</button>
                <button className={`tab-button tab-button-sub ${activeTab === 'calidad-observaciones' ? 'active' : ''}`} onClick={() => setActiveTab('calidad-observaciones')}>Observaciones R0</button>
                <button className={`tab-button tab-button-sub ${activeTab === 'calidad-seguimiento' ? 'active' : ''}`} onClick={() => setActiveTab('calidad-seguimiento')}>ITO R1 - RF</button>
              </>
            )}
          </>
        )}
      </div>

      <div className="sidebar-bottom-buttons">
        {usuarioActual && (usuarioActual.rol === 'admin' || usuarioActual.rol === 'superadmin') && (
          <AdminSidebarSection activeTab={activeTab} setActiveTab={setActiveTab} />
        )}

        {!modoGlobal && (
          <div style={{ display: 'flex', gap: '4px' }}>
            <button className={`tab-button ${activeTab === 'proyecto' ? 'active' : ''}`} onClick={() => setActiveTab('proyecto')} style={{ flex: 1 }}>Proyecto</button>
            <button className={`tab-button ${activeTab === 'equipo' ? 'active' : ''}`} onClick={() => setActiveTab('equipo')} style={{ flex: 1 }}>Equipo ({teamMembers.length})</button>
          </div>
        )}
        <button className={`tab-button ${activeTab === 'configuracion' ? 'active' : ''}`} onClick={() => setActiveTab('configuracion')}>Configuración</button>
        {!modoGlobal && (
          <button className={`tab-button ${activeTab === 'ayuda' ? 'active' : ''}`} onClick={() => setActiveTab('ayuda')}>Ayuda</button>
        )}
        {onLogout && (
          <button className="sidebar-logout-button" onClick={onLogout}>
            Cerrar Sesión
          </button>
        )}
      </div>
    </aside>
  );
};

const adminSubTabs = [
  { key: 'admin-dashboard', label: 'Dashboard' },
  { key: 'admin-usuarios', label: 'Usuarios' },
  { key: 'admin-logs', label: 'Logs de Auditoría' },
  { key: 'admin-enviar-correos', label: 'Enviar Correos' },
  { key: 'admin-apps', label: 'Apps por Proyecto' }
];

const AdminSidebarSection = ({ activeTab, setActiveTab }) => {
  const isAdminActive = activeTab.startsWith('admin');
  const [expandido, setExpandido] = useState(isAdminActive);

  const handleToggle = () => {
    if (!isAdminActive) {
      setActiveTab('admin-dashboard');
      setExpandido(true);
    } else {
      setExpandido(prev => !prev);
    }
  };

  return (
    <>
      <button
        className={`tab-button ${isAdminActive ? 'active' : ''}`}
        onClick={handleToggle}
        style={{
          background: '#111827', color: '#ffffff', fontWeight: '700',
          border: '2px solid #111827', boxShadow: '0 2px 8px rgba(30, 58, 95, 0.25)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}
        title="Panel de administración"
      >
        Gestión de Usuarios
        <span style={{ fontSize: '10px', marginLeft: '8px' }}>{expandido && isAdminActive ? '▼' : '▶'}</span>
      </button>
      {expandido && isAdminActive && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingLeft: '12px' }}>
          {adminSubTabs.map(item => (
            <button
              key={item.key}
              className={`tab-button ${activeTab === item.key ? 'active' : ''}`}
              onClick={() => setActiveTab(item.key)}
              style={{
                fontSize: '12px', padding: '6px 10px',
                background: activeTab === item.key ? 'rgba(30, 58, 95, 0.35)' : 'rgba(30, 58, 95, 0.1)',
                color: '#111827', fontWeight: activeTab === item.key ? '700' : '500',
                border: activeTab === item.key ? '1px solid rgba(30, 58, 95, 0.5)' : '1px solid rgba(30, 58, 95, 0.15)',
                borderRadius: '0'
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default Sidebar;
